import { Component, JSX } from 'solid-js';
import type {
  ControlDropdownShellContext,
  SplitControlDropdownProps,
} from './control-dropdown.types';
import { ControlDropdownShell } from './ControlDropdownShell';
import { useLastToolSelection } from './useLastToolSelection';
import { AnchorIcon } from './AnchorIcon';

/**
 * Split tool picker: the primary (icon) button replays the last-used tool (or
 * deactivates when active), while the caret opens the menu. Use for toolbars
 * where a group of related tools shares one slot (e.g. measurement tools).
 * For a plain menu picker, use {@link ControlDropdown} instead.
 */
export const SplitControlDropdown: Component<SplitControlDropdownProps> = (
  props
) => {
  const { menuItems, activateLastSelection } = useLastToolSelection({
    items: () => props.items,
    storageKey: () => props.storageKey,
  });

  const hoverClass = (): string =>
    props.pressed || props.isAnchorActive
      ? ''
      : 'hover:bg-[var(--modus-wc-color-base-100)]';

  const renderAnchor = (ctx: ControlDropdownShellContext): JSX.Element => {
    const handleIconClick = (): void => {
      if (props.isAnchorActive && props.onAnchorActiveClick) {
        props.onAnchorActiveClick();
        ctx.close();
        return;
      }
      activateLastSelection();
      ctx.close();
    };

    return (
      <section
        class={ctx.anchorSectionClass()}
        data-testid="control-dropdown-anchor"
      >
        <button
          type="button"
          data-testid="control-dropdown-anchor-icon"
          class={`${ctx.anchorButtonHeightClass()} flex items-center justify-center cursor-pointer rounded-sm ${hoverClass()}`}
          onClick={handleIconClick}
        >
          <div class="pl-1 flex items-center">
            <AnchorIcon anchorIcon={props.anchorIcon} size={props.size} />
          </div>
        </button>
        <span
          class="w-px self-stretch opacity-30 bg-current"
          data-testid="control-dropdown-anchor-divider"
        />
        <button
          type="button"
          data-testid="control-dropdown-anchor-caret"
          class={`${ctx.anchorButtonHeightClass()} flex items-center pr-0.5 cursor-pointer rounded-sm ${hoverClass()}`}
          onClick={() => ctx.toggle()}
        >
          <i
            class={`icon-font tc-icon-arrow-drop-down transition-all duration-300 ${
              ctx.isOpen() ? 'rotate-180' : ''
            }`}
          />
        </button>
      </section>
    );
  };

  return (
    <ControlDropdownShell
      items={menuItems()}
      position={props.position}
      tooltip={props.tooltip}
      pressed={props.pressed}
      isAnchorActive={props.isAnchorActive}
      size={props.size}
    >
      {(ctx) => renderAnchor(ctx)}
    </ControlDropdownShell>
  );
};
